import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Dialog, Flex, Modal, Spacer, Text, Tree,
} from '@oliasoft-open-source/react-ui-library';
import { TbFolder, TbFolderOpen } from 'react-icons/tb';
import translations from 'client/internationalization/translation-map.json';
import { IFilesBrowserItem } from './files-browser.utils';
import { MoveTarget, ROOT_FOLDER_ID } from './move-targets';

// Tree reserves id 0 for its own root, which is also the "Files" root folder id
const ROOT_TREE_ITEM_ID = 'move-to-root';

type MoveToModalProps = {
  item: IFilesBrowserItem;
  targets: MoveTarget[];
  onMove: (item: IFilesBrowserItem, targetFolderId: string | number) => void;
  onClose: () => void;
};

export const MoveToModal = ({
  item, targets, onMove, onClose,
}: MoveToModalProps) => {
  const { t } = useTranslation();
  const [selectedTarget, setSelectedTarget] = useState<MoveTarget>();

  const toTreeItem = (target: MoveTarget, parent: string | number) => {
    const treeItemId = target.id === ROOT_FOLDER_ID ? ROOT_TREE_ITEM_ID : target.id;
    return {
      id: treeItemId,
      parent,
      name: (
        <Flex alignItems="center">
          <Text muted={target.isDisabled}>{target.name}</Text>
          {target.isCurrent && (
            <>
              <Spacer width="var(--padding-xs)" />
              <Text faint small>{t(translations.current)}</Text>
            </>
          )}
        </Flex>
      ),
      active: selectedTarget?.id === target.id,
      disabled: target.isDisabled,
      onClick: () => {
        if (!target.isDisabled) {
          setSelectedTarget(target);
        }
      },
    };
  };

  const toTreeItems = (folders: MoveTarget[], parent: string | number): ReturnType<typeof toTreeItem>[] => {
    return folders.flatMap((folder) => {
      return [toTreeItem(folder, parent), ...toTreeItems(folder.children, folder.id)];
    });
  };

  const [root, ...topLevelFolders] = targets;
  const visibleTargets = root.isDisabled ? topLevelFolders : targets;
  const treeItems = toTreeItems(visibleTargets, 0);
  const hasChildren = (treeItemId: string | number) => {
    return treeItems.some((treeItem) => treeItem.parent === treeItemId);
  };

  const handleMove = () => {
    if (selectedTarget) {
      onMove(item, selectedTarget.id);
      onClose();
    }
  };

  return (
    <Modal visible centered onEscape={onClose}>
      <Dialog
        dialog={{
          heading: t(translations.moveTo),
          width: '400px',
          scroll: true,
          content: (
            <Tree
              list={{ items: treeItems, noHeader: true }}
              itemHasChild={(node) => hasChildren(node.id)}
              icons={{
                expand: <TbFolder />,
                collapse: <TbFolderOpen />,
              }}
            />
          ),
          contentPadding: 0,
          footer: (
            <>
              <Button colored label={t(translations.move)} disabled={!selectedTarget} onClick={handleMove} />
              <Button label={t(translations.cancel)} onClick={onClose} />
            </>
          ),
          onClose,
        }}
      />
    </Modal>
  );
};
